import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, BookOpen, Plus, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { HierarchyBrowser } from '../components/HierarchyBrowser';
import { cn } from '../utils/cn';
import { useTutorials } from '../hooks/useApi';
import { usePlatformTranslation } from '../hooks/usePlatformTranslation';
import { useLanguage } from '../contexts/LanguageContext';

const TutorialsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Language filter state
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  
  // Use the API hooks to fetch data
  const { currentLanguage } = useLanguage();
  const { data: tutorialsData, loading, error, refetch } = useTutorials(1, 12, selectedLanguage);
  const { t } = usePlatformTranslation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-xl mx-auto mb-4 animate-pulse" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mx-auto mb-4 animate-pulse" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-96 mx-auto animate-pulse" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4" />
                  <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
                  <div className="flex justify-between">
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Error Loading Tutorials
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Header with Search */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Title */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-[#667eea] to-[#764ba2] rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {t('nav.tutorials')} & Guides
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">
                  Browse and explore tutorials
                </p>
              </div>
            </div>

            {/* Search and Actions */}
            <div className="flex items-center space-x-3 flex-1 max-w-md ml-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tutorials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent bg-gray-50"
                />
              </div>
              
              {/* Language Filter - Compact */}
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setSelectedLanguage('all')}
                  className={cn(
                    'px-2 py-1 text-xs font-medium rounded transition-colors',
                    selectedLanguage === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  All
                </button>
                <button
                  onClick={() => setSelectedLanguage('en')}
                  className={cn(
                    'px-2 py-1 text-xs font-medium rounded transition-colors',
                    selectedLanguage === 'en'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  🇺🇸
                </button>
                <button
                  onClick={() => setSelectedLanguage('es')}
                  className={cn(
                    'px-2 py-1 text-xs font-medium rounded transition-colors',
                    selectedLanguage === 'es'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  🇪🇸
                </button>
              </div>

              {/* Create Button */}
              <motion.button
                onClick={() => {
                  // Trigger modal via custom event
                  window.dispatchEvent(new CustomEvent('openCreateContentModal'));
                }}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all shadow-sm hover:shadow-md text-sm font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">{t('tutorial.createNew')}</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <HierarchyBrowser />
      </div>
    </div>
  );
};

export default TutorialsPage;
